import { Controller } from "@hotwired/stimulus"
import { useDebounce, useResize } from "stimulus-use";
import { post } from '@rails/request.js'
// import microWait from "../micro_wait";

// Connects to data-controller="ui"
export default class extends Controller {
  // param for sidebar-breakpoint
  static values = {
    sidebarBp: { type: Number, default: 1280 },
    expanded: { type: Boolean, default: false },
    prefUrl: { type: String }
  }

  static targets = [
    'header', 'overlay',
    'navLink', 'bpData'
  ]

  static classes = ['expanded']

  static debounces = ['navUpdate']

  connect() {
    useResize(this)
    useDebounce(this, { wait: 50 })

    app.ui = this
    window.ui = this
    this.lastWidth = this.viewportWidth

    // Keep registrations across reconnects. Wiping here drops modals that
    // are still connected, and the next openModal call throws.
    this.modals ??= {}

    document.addEventListener("ui#openModal", this.openModal.bind(this))
    document.addEventListener("ui#closeModal", this.closeModal.bind(this))

    document.addEventListener(
      "turbo:before-cache",
      this.beforeTurboCache.bind(this),
    )
  }

  // Handle Turbo Events
  //--------------------------------------------------------------------------//

  beforeTurboCache() {
    /**
     * We don't want a modal in the open state to be cached by Turbo, because
     * it will be visible for an instant when loading a recently visited page.
     */
    for (const mdl in this.modals) {
      this.modals[mdl].close();
    }
  }

  // Stimulus-use callbacks
  //--------------------------------------------------------------------------//

  resize({ width }) {
    this.resizeWidth = width
  }

  // Target Callbacks
  //--------------------------------------------------------------------------//

  navLinkTargetConnected() {
    this.navUpdate()
  }

  // General UI Controls
  //--------------------------------------------------------------------------//

  showOverlay(event) {
    event?.preventDefault()

    this.overlay.show()
  }

  hideOverlay(event) {
    event?.preventDefault()

    this.overlay.hide()
  }

  /*
    Useful for if something changes the width of chart containers, but the
    window wasn't resized.
  */
  resizeCharts() {
    if (!app?.graphs) { return }

    for (const [name, ctrlr] of Object.entries(app.graphs)) {
      ctrlr.resize()
    }
  }

  // Open a named modal
  openModal(event) {
    if (event.metaKey || event.ctrlKey) { return false }

    const modal = this.modalNamed(event, 'openModal')
    if (!modal) return

    modal.open(event)
  }

  closeModal(event) {
    const modal = this.modalNamed(event, 'closeModal')
    if (!modal) return

    modal.close(event)
  }

  // Modals register themselves in connect(). A click or turbo stream can
  // arrive for a name that is not on the page, or after the registry was
  // cleared. Calling .open/.close on that miss is the production TypeError.
  modalNamed(event, action) {
    const name = event?.params?.name || event?.detail?.name || 'main'
    const modal = this.modals?.[name]

    if (!modal) {
      console.warn(`[ui] ${action}: no modal registered as "${name}"`)
      return
    }

    return modal
  }

  // Open a named dialog (modal replacement)
  openDialog(event) {
    const name = event?.params?.name || event?.detail?.name || 'main'
    this.dialogs[name].open(event)
  }

  closeDialog(event) {
    const name = event?.params?.name || event?.detail?.name || 'main'
    this.dialogs[name].close(event)
  }



  // Command Palette
  //--------------------------------------------------------------------------//

  openPalette(event) {
    event?.preventDefault()
    app.cmd?.open()
  }

  // Sidebar / App-expanded Management
  //--------------------------------------------------------------------------//

  /**
   * Sidebar visibility is generally handled by CSS see _app_layout.css
   * Below are functions to allow the user to expand the main area by hiding
   * the sidebar.
   */

  expand(event) {
    event?.preventDefault()

    this.expandedValue = true

    if (this.expandedClass) {
      this.element.classList.add(...this.expandedClasses)
      this.headerTarget.classList.add(...this.expandedClasses)

      // Prevent the brief flash of prior layout
      Turbo.cache.clear()
    }

    this.storePreferences()

    // graph_controller listens for this to resize charts
    this.dispatch('expanded')
  }

  collapse(event) {
    event?.preventDefault()

    this.expandedValue = false

    if (this.expandedClass) {
      this.element.classList.remove(...this.expandedClasses)
      this.headerTarget.classList.remove(...this.expandedClasses)

      // Prevent the brief flash of prior layout
      Turbo.cache.clear()
    }

    this.storePreferences()
    this.dispatch('collapsed')
  }

  /**
   * The current breakpoint is less than 'xl'
   */
  get shouldHideSidebar() {
    return this.twBreakpoints.indexOf(this.currentBreakpoint) < this.twBreakpoints.indexOf('xl')
  }

  /**
   * Post the user's UI preferences to Rails. This stores simple values in
   * the user session object.
   * (There's only ui_expanded for now)
   */
  storePreferences() {
    post(this.prefUrlValue, {
      body: { preferences: { ui_expanded: this.expandedValue } }
    })
  }

  // React to page topics
  //--------------------------------------------------------------------------//

  /**
   * Called from page_controller. When a page controller connects it calls up
   * to the UI to update all the listening elements in the app nav.
   */
  navUpdate() {
    if (this?.page?.topics) {
      this.dispatch('navUpdate', { bubbles: true, detail: { topics: this.page.topics } })
    }
  }

  // Utils
  //--------------------------------------------------------------------------//

  get viewportWidth() {
    return window.visualViewport.width
  }

  /**
   * Current Tailwind breakpoint smuggled from CSS
   * See _bp_data.css
   */
  get currentBreakpoint() {
    return window.getComputedStyle(this.bpDataTarget, ':before')
      .getPropertyValue('content')
      .replaceAll('"', '')
  }

  get twBreakpoints() {
    return ['initial', 'sm', 'md', 'lg', 'xl', '2xl']
  }

  get isXlScreen() {
    return this.currentBreakpoint == 'xl'
  }
}
